/**
 * The action types on the transfer queue, and what each one is validated against.
 *
 * Pure. No React, no hooks, no catalogue — so the grouping, the ordering and the two
 * findings can be asserted without a render, and so `exports.ts` can order the exported
 * file by the same rule the screen groups by without pulling a component into the export
 * bundle. The surfaces that render this are in `transfer-actions.tsx`.
 *
 * ── THE SPLIT IS THE PLATFORM'S, NOT THIS SCREEN'S ──────────────────────────
 *
 * Legacy's `rebalance-input-app` carried five tabs — `GroupActionsCompact`, `Pause`,
 * `Prices`, `Push`, `Recall` — one editable grid per action type, each with its own
 * validation. Doc 21 §7 reads the pattern as the finding: *"lumping every action type
 * into one table loses the distinct validation rules and review needs each one has"*.
 * Doc 22 UI8 asks for the pattern, and is explicit that the surface is internal/ops
 * tooling rather than anything a store manager touches.
 *
 * **The five legacy categories are not carried over, because this platform does not
 * publish them.** `TransferSummary.purpose` is what the in-season contract actually has,
 * and it is the real distinction: `rebalance` moves stock that is in the wrong place,
 * `run_repair` puts a broken size run back together, `consolidate` gathers remnants.
 * Three different review questions, which is what UI8 is about. Forcing
 * Compact/Pause/Prices/Push/Recall onto that would be inventing four categories no read
 * publishes and losing the one it does.
 *
 * `purpose` is a bare `string` in the pinned spec — no enum, no `$ref` — so the
 * vocabulary is DATA (G5) and a purpose this build has no label for gets **its own
 * group**, headed by the code itself. Folding unknown codes into one "other" bucket would
 * merge two distinguishable action types behind one word, which is the defect this module
 * exists to remove.
 *
 * ── THE TWO FINDINGS, AND WHY THEY ARE PER GROUP ────────────────────────────
 *
 * reqs/07 §3.4: *"a transfer is only proposed when the incremental full-price sale
 * exceeds the transfer cost"*. `net_benefit` is that netting — incremental full-price
 * margin less lane cost, handling and donor run damage — so a **published** transfer that
 * does not clear zero contradicts the rule that produced it. It is reported per group
 * rather than once for the queue because the remedy differs by purpose: a `rebalance`
 * below the hurdle is a lane priced wrong, a `run_repair` below it is a repair whose
 * donor damage outweighs the run it mends, and a reviewer told "four transfers do not
 * clear" without being told which kind cannot act on it.
 *
 * The second is arithmetic rather than commercial: a group whose rows carry more than one
 * `currency_code` **cannot be totalled**, and nothing here converts. The estate's standing
 * rule is `land_lane_rate_cards`' — amounts land as sent and are never converted at read,
 * because a missing FX row in ClickHouse yields 0 rather than an error and zero freight
 * makes every transfer look profitable. So a mixed-currency group withholds its total and
 * says why, instead of printing one in a currency it picked.
 *
 * The four grid columns and their lineage this module used to declare went with the per-type
 * grids (`docs/adr/6000`, amendment of 2026-09-30): the rows are drawn once, in the transfer
 * table, whose columns declare their own polarity.
 *
 * See `docs/adr/6000`.
 */

import { isNamedPurpose, NAMED_PURPOSES, toPurposeKey, type TransferRow } from './api';

/** One published action type, and this run's transfers of that type. */
export interface TransferActionGroup {
  /** The platform's own code, as published. Reference data, never translated. */
  readonly purpose: string;
  /** The catalogue key for the code, or `undefined` where this build has none. */
  readonly labelKey: string | undefined;
  readonly rows: readonly TransferRow[];
}

/**
 * The catalogue key for a purpose, or `undefined` where this build has no label for it.
 *
 * `toPurposeKey` falls back to a labelled `unclassified` key, which is right in a cell —
 * the reader sees there is a purpose and that it is unnamed — and wrong as a section
 * heading, because two unrecognised codes would then head two sections with identical
 * titles. An unnamed purpose heads its section with the code itself, which is the ruling
 * `dispositions.tsx` already takes on an unnamed reason code.
 */
export function purposeLabelKey(purpose: string): string | undefined {
  return isNamedPurpose(purpose) ? toPurposeKey(purpose) : undefined;
}

/**
 * The run's transfers, split by the action type the platform published for each.
 *
 * Named purposes first, in `NAMED_PURPOSES` order — the order a planner works, and a
 * property of the vocabulary rather than of whichever page came back first. Codes this
 * build does not name follow, sorted by the code, each in a group of its own. Within a
 * group the transfers are ordered by netting, largest first, so the lane that pays most
 * is the first one read.
 *
 * A named purpose with no transfers in this run is **not** emitted: an empty section
 * headed "run repair" reads as a kind of work that exists and has nothing in it, which is
 * a claim about the run this function cannot make from the rows it was given.
 */
export function toTransferActionGroups(
  rows: readonly TransferRow[],
): readonly TransferActionGroup[] {
  const byPurpose = new Map<string, TransferRow[]>();
  for (const row of rows) {
    const existing = byPurpose.get(row.purpose);
    if (existing === undefined) byPurpose.set(row.purpose, [row]);
    else existing.push(row);
  }
  const rank = (purpose: string): number => {
    const index = NAMED_PURPOSES.indexOf(purpose);
    return index === -1 ? NAMED_PURPOSES.length : index;
  };
  return [...byPurpose.entries()]
    .map(([purpose, group]) => ({
      purpose,
      labelKey: purposeLabelKey(purpose),
      rows: [...group].sort((a, b) => b.netBenefit - a.netBenefit) as readonly TransferRow[],
    }))
    .sort((a, b) => rank(a.purpose) - rank(b.purpose) || a.purpose.localeCompare(b.purpose));
}

/**
 * The transfers in a group that do not clear their own cost.
 *
 * reqs/07 §3.4's hurdle, applied to what was published. Zero is included: a transfer
 * whose incremental sale exactly equals its cost does not *exceed* it, and the rule says
 * exceeds.
 */
export function belowHurdle(rows: readonly TransferRow[]): readonly TransferRow[] {
  return rows.filter((row) => row.netBenefit <= 0);
}

/**
 * The one currency a group is denominated in, or `null` where its rows disagree.
 *
 * `null` is not a failure: it is the statement that this group cannot be totalled without
 * a conversion, and nothing here converts. An empty group answers `null` too, and never
 * reaches a grid.
 */
export function groupCurrency(rows: readonly TransferRow[]): string | null {
  const first = rows[0];
  if (first === undefined) return null;
  return rows.every((row) => row.currencyCode === first.currencyCode) ? first.currencyCode : null;
}

/**
 * The group's netting, summed — only where every row shares a currency.
 *
 * `null` where they do not, for the reason above. A number here that silently added COP
 * to USD would be the same defect as a zero freight cost: legible, wrong, and acted on.
 */
export function groupNetBenefit(rows: readonly TransferRow[]): number | null {
  if (groupCurrency(rows) === null) return null;
  return rows.reduce((total, row) => total + row.netBenefit, 0);
}

/** The purposes in the order a planner works: the named ones in `NAMED_PURPOSES` order, then the others by code. Pure. */
export function orderedPurposes(purposes: readonly string[]): readonly string[] {
  const rank = (purpose: string): number => {
    const index = NAMED_PURPOSES.indexOf(purpose);
    return index === -1 ? NAMED_PURPOSES.length : index;
  };
  return [...purposes].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}
